import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import { ViewDetailsItemT } from 'shared/containers/ViewDetails/ViewDetails';
import SCHCTemplate from 'shared/models/SCHCTemplate';
import jsonObj from 'utils/objects';
import { generateRandomString } from 'utils/string';

import SCHCTemplatesDetailsAccordionLayout from './SCHCTemplatesDetailsAccordionLayout';

type SCHCTemplatesDetailsRenderFragmentationProps = {
  data: SCHCTemplate.API.Fragmentation.RuleT | undefined;
}
function SCHCTemplatesDetailsRenderFragmentation({
  data,
}: SCHCTemplatesDetailsRenderFragmentationProps) {
  const contentOnMode = (
    condition: 'OnACK' | 'OnACKAlways' | 'OnACKError' | 'OnOptimizedACK',
    value: string,
  ): string => {
    const mode = data?.fragmentationParam.mode;
    if (condition === 'OnACK') {
      if (mode !== SCHCTemplate.API.Fragmentation.Mode.NoACK) {
        return value;
      }
    }
    if (condition === 'OnACKAlways') {
      if (mode === SCHCTemplate.API.Fragmentation.Mode.ACKAlways) {
        return value;
      }
    }
    if (condition === 'OnACKError') {
      if (mode === SCHCTemplate.API.Fragmentation.Mode.ACKOnError) {
        return value;
      }
    }
    if (condition === 'OnOptimizedACK') {
      if (mode === SCHCTemplate.API.Fragmentation.Mode.OptimizedACKOnError) {
        return value;
      }
    }
    return '';
  };
  const formatUnit = (value: any, unit: string) => `${value} ${unit}${value === 0 ? '' : 's'}`;
  const itemsStructure: ViewDetailsItemT[] = [
    {
      label: 'Fragmentation mode',
      dataPath: 'fragmentationParam.mode',
    },
    {
      label: 'Direction',
      dataPath: 'direction',
      renderFn: (value: string) => (value === 'FRAG_UPLINK' ? 'Uplink' : 'Downlink'),
    },
    {
      label: 'Rule ID size',
      dataPath: 'fragmentationParam.ruleIdSize',
      renderFn: (value: string) => formatUnit(value, 'bit'),
    },
    {
      label: 'DTag field size',
      dataPath: 'fragmentationParam.dTagSize',
      renderFn: (value: string) => formatUnit(value, 'bit'),
    },
    {
      label: 'Window size',
      dataPath: 'fragmentationParam.winSize',
    },
    {
      label: 'MIC algorithm type',
      dataPath: 'fragmentationParam.micAlgo',
    },
    {
      label: 'Inactivity timer',
      dataPath: 'fragmentationParam.inactivityTime',
      renderFn: (value: string) => formatUnit(value, 'second'),
    },
    {
      label: 'W field size',
      dataPath: 'fragmentationParam.wSize',
      renderFn: (value: string) => contentOnMode('OnACK', formatUnit(value, 'bit')),
    },
    {
      label: 'Retransmission timer',
      dataPath: 'fragmentationParam.retransmissionTime',
      renderFn: (value: string) => contentOnMode('OnACK', formatUnit(value, 'second')),
    },
    {
      label: 'All-1 Retransmission timer',
      dataPath: 'fragmentationParam.all1RetransmissionTime',
      renderFn: (value: string) => contentOnMode('OnACK', formatUnit(value, 'second')),
    },
    {
      label: 'DTag Release timer',
      dataPath: 'fragmentationParam.dTagReleaseTime',
      renderFn: (value: string) => contentOnMode('OnACK', formatUnit(value, 'second')),
    },
    {
      label: 'Max ACK',
      dataPath: 'fragmentationParam.maxAck',
      renderFn: (value: string) => contentOnMode('OnACK', value),
    },
    {
      label: 'Short Session Retransmission Time',
      dataPath: 'fragmentationParam.enableShortSessionRetransmissionTime',
      renderFn: (value: string) => contentOnMode('OnACKAlways', value ? 'Yes' : 'No'),
    },
    {
      label: 'Tile size',
      dataPath: 'fragmentationParam.tileSize',
      renderFn: (value: string) => (
        contentOnMode('OnACKError', formatUnit(value, 'bit'))
      ),
    },
    {
      label: 'Disable All 1 Payload',
      dataPath: 'fragmentationParam.aoeSpecs.disableAll1Payload',
      renderFn: (value: string) => (
        contentOnMode('OnACKError', value ? 'Yes' : 'No')
      ),
    },
    {
      label: 'Send ACK every window',
      dataPath: 'fragmentationParam.aoeSpecs.sendAckEveryWindow',
      renderFn: (value: string) => (
        contentOnMode('OnACKError', value ? 'Yes' : 'No')
      ),
    },
    {
      label: 'Enable Implicit ACK',
      dataPath: 'fragmentationParam.optaoeSpecs.enableImplicitAck',
      renderFn: (value: string) => (
        contentOnMode('OnOptimizedACK', value ? 'Yes' : 'No')
      ),
    },
    {
      label: 'Send ACK every window',
      dataPath: 'fragmentationParam.optaoeSpecs.sendAckEveryWindow',
      renderFn: (value: string) => (
        contentOnMode('OnOptimizedACK', value ? 'Yes' : 'No')
      ),
    },
    {
      label: 'Quality of Service',
      dataPath: 'fragmentationParam.qos',
      renderFn: (value: string) => (data?.direction === 'FRAG_UPLINK' ? value : ''),
    },
  ];
  if (!data) {
    return null;
  }
  return (
    <SCHCTemplatesDetailsAccordionLayout title={`Rule #${data.ruleId} · Fragmentation · ${data.fragmentationParam.mode}`}>
      <TableContainer component={Paper}>
        <Table aria-label="table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="center">Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              itemsStructure.map((item) => {
                const itemValue = jsonObj.getDescendantProp(data, item.dataPath as string);
                const toRender = item.renderFn ? item.renderFn(itemValue) : itemValue;
                if (toRender === '') return null;
                return (
                  <TableRow key={generateRandomString()}>
                    <TableCell>{item.label}</TableCell>
                    <TableCell align="center">{toRender}</TableCell>
                  </TableRow>
                );
              })
            }
          </TableBody>
        </Table>
      </TableContainer>
    </SCHCTemplatesDetailsAccordionLayout>
  );
}

export default SCHCTemplatesDetailsRenderFragmentation;
